import { NFL_TEAMS_TOTAL, NFL_WEEKS_TOTAL, Season, TeamNamesByID } from '@shared/constants';
import { breakSeasonWeekID, toNumber, toSeasonWeekID, toWeekID } from '@shared/helpers';
import { BestRankHighOrLow, FormatUnitType, IRanking, ITeamWeekRank, Maybe, TeamID } from '@shared/types';
import {
  CategoryName,
  CategoryNameT,
  GameWeek,
  IRankingCategories,
  IWeekRank,
  RankingsPayload,
  SeasonWeekID,
} from 'types';
import { sortTeamsByValue } from './SortHelpers';

export const getFormatUnitByCategory = (
  categoryName: string,
  defaultValue: FormatUnitType = 'plays',
): FormatUnitType => {
  // console.log(`getFormatUnitByCategory %c ${categoryName} %c`, Cls.Info, '');

  switch (categoryName) {
    case 'Allowed Passing TDs':
    case 'Allowed Rushing TDs':
    case 'Allowed Total TDs':
    case 'Defensive TDs':
    case 'Scored Passing TDs':
    case 'Scored Rushing TDs':
    case 'Scored Total TDs':
    case 'Total Scoring Passing TDs Against':
    case 'Total Scoring Passing TDs For':
    case 'Total Scoring Rushing TDs Against':
    case 'Total Scoring Rushing TDs For':
    case 'Total TDs Allowed':
    case 'Total TDs Scored':
      return 'tds';
    case 'Allowed FGs':
    case 'Scored FGs':
      return 'fgs';
    case 'Pass Attempts':
    case 'Rushing Attempts':
      return 'attempts';
    case 'Defensive Interceptions':
    case 'Interceptions Thrown':
    case 'Total Defensive Interceptions':
    case 'Total Interceptions Thrown':
      return 'int';
    case 'Fumbles Lost':
    case 'Fumbles Recovered':
      return 'fum';
    case 'Pass Completions':
    case 'Penalties':
    case 'Sacks':
    case 'Safeties':
    case 'Total Plays':
      return 'plays';
    case 'Pass Completion Percentage':
    case 'Passing Percentage':
    case 'Red Zone Efficiency':
    case 'Third Down Efficiency':
    case 'Fourth Down Efficiency':
    case 'percentage':
      return 'percent';
    case 'Allowed 2pt Conversions':
    case 'Allowed Extra Points':
    case 'Extra Points Scored':
    case 'Points Allowed':
    case 'Points Scored':
    case 'Scored 2pt Conversions':
    case 'Total Points Allowed':
    case 'Total Points Scored':
      return 'points';
    case 'Time of Possession':
      return 'time';
    case 'Passing Yards Per Attempt':
    case 'Rushing Yards Per Attempt':
    case 'Total Yards Per Play':
    case 'Yards Per Play':
      return 'ratio';
    case 'Passing Yards Allowed':
    case 'Passing Yards':
    case 'Penalty Yards':
    case 'Rushing Yards Allowed':
    case 'Rushing Yards':
    case 'Total Passing Yards Allowed':
    case 'Total Passing Yards':
    case 'Total Rushing Yards Allowed':
    case 'Total Rushing Yards':
    case 'Total Yards Allowed':
    case 'Total Yards':
      return 'yards';
    default:
      return defaultValue;
  }
};

export const getHighLow = (categoryName: string): BestRankHighOrLow => {
  switch (categoryName) {
    case 'totalPointsAllowed':
    case 'totalYardsAllowed':
    case 'totalPassingYardsAllowed':
    case 'totalRushingYardsAllowed':
    case 'totalInterceptionsThrown':
    case 'totalFumblesLost':
    case 'totalPenalties':
    case 'totalPenaltyYards':
    case 'totalSackedTimes':
    case 'totalSackedYardsLost':
    case 'totalScoringPassingTDAgainst':
    case 'totalScoringRushingTDAgainst':
    case 'totalScoringFGAgainst':
    case 'totalScoringEPAgainst':
    case 'totalScoring2PtAgainst':
    case 'totalScoringTotalTDAgainst':
      return 'Low';
    default:
      return 'High';
  }
};

export const getFormatUnitTypeByCategoryName = (categoryName: string): FormatUnitType => {
  switch (categoryName) {
    case 'totalYards':
    case 'totalPassingYards':
    case 'totalRushingYards':
    case 'totalPenaltyYards':
    case 'totalYardsAllowed':
    case 'totalPassingYardsAllowed':
    case 'totalRushingYardsAllowed':
    case 'totalSackedYardsLost':
      return 'yards';
    case 'totalYardsPerPlay':
    case 'totalPassingYardsPerAttempt':
    case 'totalRushingYardsPerAttempt':
      return 'ratio';

    case 'totalPassAttempts':
    case 'totalRushingAttempts':
    case 'totalThirdDownAttempts':
    case 'totalRedZoneAttempts':
    case 'totalFourthDownAttempts':
      return 'attempts';

    case 'totalPassCompletions':
    case 'totalRedZoneScored':
    case 'totalThirdDownConverted':
    case 'totalFourthDownScored':
      return 'completions';

    case 'totalPassingPercentage':
    case 'totalThirdDownEfficiency':
    case 'totalRedZoneEfficiency':
    case 'totalFourthDownEfficiency':
      return 'percent';

    case 'totalPointsScored':
    case 'totalPointsAllowed':
    case 'totalScoringEPFor':
    case 'totalScoring2PtFor':
    case 'totalScoringEPAgainst':
    case 'totalScoring2PtAgainst':
      return 'points';

    case 'totalPlays':
    case 'totalDrives':
    case 'totalFirstDownsFromPenalties':
    case 'totalFirstDownsFromRushing':
    case 'totalFirstDownsFromPassing':
    case 'totalFirstDowns':
      return 'plays';

    case 'totalInterceptionsThrown':
    case 'totalFumblesLost':
    case 'totalPenalties':
    case 'totalSacks':
    case 'totalSackedTimes':
    case 'totalDefensiveInterceptions':
    case 'totalForcedFumbles':
    case 'totalSafeties':
      return 'units';

    case 'totalTimeOfPossession':
      return 'time';

    case 'totalDefensiveOrSpecialTeamsTds':
    case 'totalScoringPassingTDFor':
    case 'totalScoringRushingTDFor':
    case 'totalScoringPassingTDAgainst':
    case 'totalScoringRushingTDAgainst':
    case 'totalScoringTotalTDFor':
    case 'totalScoringTotalTDAgainst':
      return 'tds';

    case 'totalScoringFGFor':
    case 'totalScoringFGAgainst':
      return 'fgs';

    default:
      return 'units';
  }
};

const transformByeWeekTeamsToWeeklyCategoryStat = (
  category: IWeekRank,
  byeWeekTeam: IRanking,
  formatUnit: FormatUnitType = 'plays',
  highOrLow: BestRankHighOrLow = 'Low',
): ITeamWeekRank => ({
  rankCategory: category.rankCategory,
  teamID: byeWeekTeam.teamID,
  teamName: byeWeekTeam.teamName,
  value: 0,
  rank: 0,
  week: category.weekID,
  isRankTied: false,
  bestTeam: byeWeekTeam,
  worstTeam: byeWeekTeam,
  formatUnit: getFormatUnitByCategory(category.rankCategory, formatUnit),
  highOrLow: highOrLow,
  isByeWeek: true,
});

export const getWeeklyCategoryStat = (
  category: Maybe<IWeekRank>,
  weekKey: GameWeek,
  highOrLow: BestRankHighOrLow = 'High',
  currentByeWeekTeams: TeamID[],
): Maybe<ITeamWeekRank[]> => {
  if (typeof category === 'undefined') return;

  const ranking = category.ranking.filter((team) => !currentByeWeekTeams.includes(team.teamID));

  const bestTeamRank: IRanking = ranking[0];
  const worstTeamRank: IRanking = ranking[ranking.length - 1];

  const catWeekRanks = ranking.map((rankedTeam: IRanking, index) => {
    const firstIndex = ranking.findIndex((team) => team.value === rankedTeam.value);
    const lastIndex = ranking.findLastIndex((team) => team.value === rankedTeam.value);

    const isRankTied = firstIndex !== lastIndex && index >= firstIndex && index <= lastIndex;
    const adjustedRank = firstIndex + 1;
    const teamWeekRank: ITeamWeekRank = {
      rankCategory: category.rankCategory,
      teamID: rankedTeam.teamID,
      teamName: rankedTeam.teamName,
      value: rankedTeam.value,
      week: weekKey,
      rank: adjustedRank,
      isRankTied: isRankTied,
      bestTeam: bestTeamRank,
      worstTeam: worstTeamRank,
      formatUnit: getFormatUnitByCategory(category.rankCategory),
      highOrLow: highOrLow,
    };
    return teamWeekRank;
  });

  const byeWeekTeamsRank: ITeamWeekRank[] = currentByeWeekTeams.map((teamID) => {
    const byeWeekTeamRanking = {
      teamID: teamID,
      teamName: TeamNamesByID[parseInt(teamID)],
      value: 0,
      isRankTied: false,
    } as IRanking;
    return transformByeWeekTeamsToWeeklyCategoryStat(category, byeWeekTeamRanking);
  });

  return [...catWeekRanks, ...byeWeekTeamsRank];
};

export const getAdjustedTeamRanksForAllCategories = (
  payload: RankingsPayload,
  byeWeekTeamIDs: TeamID[],
  isSeasonRanking = false,
): RankingsPayload => {
  const { rankings, weekID, seasonRankings } = payload;

  const withAdjusted = isSeasonRanking ? { ...seasonRankings } : { ...rankings };

  const weekIDKey = weekID as GameWeek;

  // #region Stats
  const rankKeys = Object.keys(withAdjusted);
  rankKeys.forEach((key) => {
    const category = withAdjusted[key as keyof IRankingCategories];
    if (category) {
      category.adjustedRanking = getWeeklyCategoryStat(category, weekIDKey, getHighLow(key), byeWeekTeamIDs);
    }
  });

  return isSeasonRanking ? { ...payload, seasonRankings: withAdjusted } : { ...payload, rankings: withAdjusted };
};

export const addSeasonRankingByCategory = (w1: IWeekRank, w2: IWeekRank): IRanking[] => {
  const weekCategory: IRanking[] = [];
  for (let i = 1; i <= NFL_TEAMS_TOTAL; i++) {
    const teamW1 = w1.ranking.find((t) => t.teamID === String(i));
    const teamW2 = w2.ranking.find((t) => t.teamID === String(i));
    if (teamW1 || teamW2) {
      const name = teamW1?.teamName || teamW2?.teamName || '';
      const valueW1 = teamW1 ? teamW1.value : 0;
      const valueW2 = teamW2 ? teamW2.value : 0;
      weekCategory.push({ teamID: String(i), teamName: name, value: valueW1 + valueW2 });
    }
  }
  return weekCategory;
};

export const addYardsPerRankingByCategory = (totalYards: IRanking[], successPlays: IRanking[]): IRanking[] => {
  const weekCategory: IRanking[] = totalYards.map((teamYards: IRanking) => {
    const successCat = successPlays.find((t) => t.teamID === teamYards.teamID);
    if (successCat) {
      const name = teamYards.teamName;
      const multiplier = teamYards.value > successCat.value ? 100 : 10000;
      const ratio = successCat.value !== 0 ? Math.round((teamYards.value / successCat.value) * multiplier) / 100 : 0;
      return { teamID: teamYards.teamID, teamName: name, value: ratio };
    }
    return { teamID: teamYards.teamID, teamName: teamYards.teamName, value: 0 };
  });

  return weekCategory;
};

export const getEfficiencyRankingByCategory = (completions: IRanking[], attempts: IRanking[]): IRanking[] => {
  const weekCategory: IRanking[] = completions.map((completion: IRanking) => {
    const attemptsCat = attempts.find((t) => t.teamID === completion.teamID);
    if (attemptsCat) {
      const name = completion.teamName;
      const ratio = attemptsCat.value !== 0 ? Math.round((completion.value / attemptsCat.value) * 10000) / 100 : 0;
      return { teamID: completion.teamID, teamName: name, value: ratio };
    }
    return { teamID: completion.teamID, teamName: completion.teamName, value: 0 };
  });

  return weekCategory;
};

export const addAllRankingCategories = (
  w1: IRankingCategories,
  w2: IRankingCategories,
  weekID: GameWeek,
): IRankingCategories => {
  // #region Variables
  let s3TotalYards: IRanking[] = [];
  let s3Plays: IRanking[] = [];
  let s3TotalYardsPerPlay: IRanking[] = [];
  let s3PassingYards: IRanking[] = [];
  let s3PassCompletions: IRanking[] = [];
  let s3PassAttempts: IRanking[] = [];
  let s3PassingPercentage: IRanking[] = [];
  let s3PassingYardsPerAttempt: IRanking[] = [];
  let s3RushingYards: IRanking[] = [];
  let s3RushingAttempts: IRanking[] = [];
  let s3RushingYardsPerAttempt: IRanking[] = [];
  let s3PointsScored: IRanking[] = [];
  let s3InterceptionsThrown: IRanking[] = [];
  let s3FumblesLost: IRanking[] = [];
  let s3TimeOfPossession: IRanking[] = [];
  let s3Penalties: IRanking[] = [];
  let s3PenaltyYards: IRanking[] = [];
  let s3SackedTimes: IRanking[] = [];
  let s3SackedYardsLost: IRanking[] = [];
  let s3Drives: IRanking[] = [];
  let s3ThirdDownAttempts: IRanking[] = [];
  let s3ThirdDownConverted: IRanking[] = [];
  let s3ThirdDownEfficiency: IRanking[] = [];
  let s3RedZoneScored: IRanking[] = [];
  let s3RedZoneAttempts: IRanking[] = [];
  let s3RedZoneEfficiency: IRanking[] = [];
  let s3FirstDowns: IRanking[] = [];
  let s3FirstDownsFromPenalties: IRanking[] = [];
  let s3FirstDownsFromRushing: IRanking[] = [];
  let s3FirstDownsFromPassing: IRanking[] = [];
  let s3FourthDownScored: IRanking[] = [];
  let s3FourthDownAttempts: IRanking[] = [];
  let s3FourthDownEfficiency: IRanking[] = [];
  // DST
  let s3TotalYardsAllowed: IRanking[] = [];
  let s3PassingYardsAllowed: IRanking[] = [];
  let s3RushingYardsAllowed: IRanking[] = [];
  let s3PointsAllowed: IRanking[] = [];
  let s3Sacks: IRanking[] = [];
  let s3DefensiveInterceptions: IRanking[] = [];
  let s3FumblesRecovered: IRanking[] = [];
  let s3Safeties: IRanking[] = [];
  let s3DefensiveTDs: IRanking[] = [];
  let s3PassingTDFor: IRanking[] = [];
  let s3sRushingTDFor: IRanking[] = [];
  let s3sFGFor: IRanking[] = [];
  let s3sEPFor: IRanking[] = [];
  let s3s2PtFor: IRanking[] = [];
  let s3sPassingTDAgainst: IRanking[] = [];
  let s3sRushingTDAgainst: IRanking[] = [];
  let s3sFGAgainst: IRanking[] = [];
  let s3sEPAgainst: IRanking[] = [];
  let s3s2PtAgainst: IRanking[] = [];
  let s3sTotalTDFor: IRanking[] = [];
  let s3sTotalTDAgainst: IRanking[] = [];
  // #endregion

  // #region Calculations
  if (w1.totalYards && w2.totalYards && w1.totalPlays && w2.totalPlays) {
    s3TotalYards = addSeasonRankingByCategory(w1.totalYards, w2.totalYards);
    s3Plays = addSeasonRankingByCategory(w1.totalPlays, w2.totalPlays);
    s3TotalYardsPerPlay = addYardsPerRankingByCategory(s3TotalYards, s3Plays);
  }

  if (w1.totalPassingYards && w2.totalPassingYards) {
    s3PassingYards = addSeasonRankingByCategory(w1.totalPassingYards, w2.totalPassingYards);

    if (w1.totalPassCompletions && w2.totalPassCompletions && w1.totalPassAttempts && w2.totalPassAttempts) {
      s3PassCompletions = addSeasonRankingByCategory(w1.totalPassCompletions, w2.totalPassCompletions);
      s3PassAttempts = addSeasonRankingByCategory(w1.totalPassAttempts, w2.totalPassAttempts);
      s3PassingPercentage = addYardsPerRankingByCategory(s3PassCompletions, s3PassAttempts);
      s3PassingYardsPerAttempt = addYardsPerRankingByCategory(s3PassingYards, s3PassAttempts);
    }
  }

  if (w1.totalRushingYards && w2.totalRushingYards && w1.totalRushingAttempts && w2.totalRushingAttempts) {
    s3RushingYards = addSeasonRankingByCategory(w1.totalRushingYards, w2.totalRushingYards);
    s3RushingAttempts = addSeasonRankingByCategory(w1.totalRushingAttempts, w2.totalRushingAttempts);
    s3RushingYardsPerAttempt = addYardsPerRankingByCategory(s3RushingYards, s3RushingAttempts);
  }
  if (w1.totalPointsScored && w2.totalPointsScored) {
    s3PointsScored = addSeasonRankingByCategory(w1.totalPointsScored, w2.totalPointsScored);
  }
  if (w1.totalInterceptionsThrown && w2.totalInterceptionsThrown) {
    s3InterceptionsThrown = addSeasonRankingByCategory(w1.totalInterceptionsThrown, w2.totalInterceptionsThrown);
  }
  if (w1.totalFumblesLost && w2.totalFumblesLost) {
    s3FumblesLost = addSeasonRankingByCategory(w1.totalFumblesLost, w2.totalFumblesLost);
  }
  if (w1.totalTimeOfPossession && w2.totalTimeOfPossession) {
    s3TimeOfPossession = addSeasonRankingByCategory(w1.totalTimeOfPossession, w2.totalTimeOfPossession);
  }
  if (w1.totalPenalties && w2.totalPenalties) {
    s3Penalties = addSeasonRankingByCategory(w1.totalPenalties, w2.totalPenalties);
  }
  if (w1.totalPenaltyYards && w2.totalPenaltyYards) {
    s3PenaltyYards = addSeasonRankingByCategory(w1.totalPenaltyYards, w2.totalPenaltyYards);
  }
  // DST
  if (w1.totalYardsAllowed && w2.totalYardsAllowed) {
    s3TotalYardsAllowed = addSeasonRankingByCategory(w1.totalYardsAllowed, w2.totalYardsAllowed);
  }
  if (w1.totalPassingYardsAllowed && w2.totalPassingYardsAllowed) {
    s3PassingYardsAllowed = addSeasonRankingByCategory(w1.totalPassingYardsAllowed, w2.totalPassingYardsAllowed);
  }
  if (w1.totalRushingYardsAllowed && w2.totalRushingYardsAllowed) {
    s3RushingYardsAllowed = addSeasonRankingByCategory(w1.totalRushingYardsAllowed, w2.totalRushingYardsAllowed);
  }
  if (w1.totalPointsAllowed && w2.totalPointsAllowed) {
    s3PointsAllowed = addSeasonRankingByCategory(w1.totalPointsAllowed, w2.totalPointsAllowed);
  }
  if (w1.totalDefensiveInterceptions && w2.totalDefensiveInterceptions) {
    s3DefensiveInterceptions = addSeasonRankingByCategory(
      w1.totalDefensiveInterceptions,
      w2.totalDefensiveInterceptions,
    );
  }
  if (w1.totalForcedFumbles && w2.totalForcedFumbles) {
    s3FumblesRecovered = addSeasonRankingByCategory(w1.totalForcedFumbles, w2.totalForcedFumbles);
  }
  if (w1.totalSacks && w2.totalSacks) {
    s3Sacks = addSeasonRankingByCategory(w1.totalSacks, w2.totalSacks);
  }
  if (w1.totalSafeties && w2.totalSafeties) {
    s3Safeties = addSeasonRankingByCategory(w1.totalSafeties, w2.totalSafeties);
  }
  if (w1.totalDefensiveOrSpecialTeamsTds && w2.totalDefensiveOrSpecialTeamsTds)
    s3DefensiveTDs = addSeasonRankingByCategory(w1.totalDefensiveOrSpecialTeamsTds, w2.totalDefensiveOrSpecialTeamsTds);

  if (w1.totalScoringPassingTDFor && w2.totalScoringPassingTDFor)
    s3PassingTDFor = addSeasonRankingByCategory(w1.totalScoringPassingTDFor, w2.totalScoringPassingTDFor);

  if (w1.totalScoringRushingTDFor && w2.totalScoringRushingTDFor)
    s3sRushingTDFor = addSeasonRankingByCategory(w1.totalScoringRushingTDFor, w2.totalScoringRushingTDFor);

  if (w1.totalScoringFGFor && w2.totalScoringFGFor)
    s3sFGFor = addSeasonRankingByCategory(w1.totalScoringFGFor, w2.totalScoringFGFor);
  if (w1.totalScoringEPFor && w2.totalScoringEPFor)
    s3sEPFor = addSeasonRankingByCategory(w1.totalScoringEPFor, w2.totalScoringEPFor);

  if (w1.totalScoring2PtFor && w2.totalScoring2PtFor)
    s3s2PtFor = addSeasonRankingByCategory(w1.totalScoring2PtFor, w2.totalScoring2PtFor);

  if (w1.totalScoringPassingTDAgainst && w2.totalScoringPassingTDAgainst)
    s3sPassingTDAgainst = addSeasonRankingByCategory(w1.totalScoringPassingTDAgainst, w2.totalScoringPassingTDAgainst);

  if (w1.totalScoringRushingTDAgainst && w2.totalScoringRushingTDAgainst)
    s3sRushingTDAgainst = addSeasonRankingByCategory(w1.totalScoringRushingTDAgainst, w2.totalScoringRushingTDAgainst);

  if (w1.totalScoringFGAgainst && w2.totalScoringFGAgainst)
    s3sFGAgainst = addSeasonRankingByCategory(w1.totalScoringFGAgainst, w2.totalScoringFGAgainst);

  if (w1.totalScoringEPAgainst && w2.totalScoringEPAgainst)
    s3sEPAgainst = addSeasonRankingByCategory(w1.totalScoringEPAgainst, w2.totalScoringEPAgainst);

  if (w1.totalScoring2PtAgainst && w2.totalScoring2PtAgainst)
    s3s2PtAgainst = addSeasonRankingByCategory(w1.totalScoring2PtAgainst, w2.totalScoring2PtAgainst);

  if (w1.totalScoringTotalTDFor && w2.totalScoringTotalTDFor)
    s3sTotalTDFor = addSeasonRankingByCategory(w1.totalScoringTotalTDFor, w2.totalScoringTotalTDFor);

  if (w1.totalScoringTotalTDAgainst && w2.totalScoringTotalTDAgainst)
    s3sTotalTDAgainst = addSeasonRankingByCategory(w1.totalScoringTotalTDAgainst, w2.totalScoringTotalTDAgainst);

  if (w1.totalSackedTimes && w2.totalSackedTimes) {
    s3SackedTimes = addSeasonRankingByCategory(w1.totalSackedTimes, w2.totalSackedTimes);
  }

  if (w1.totalSackedYardsLost && w2.totalSackedYardsLost) {
    s3SackedYardsLost = addSeasonRankingByCategory(w1.totalSackedYardsLost, w2.totalSackedYardsLost);
  }

  if (w1.totalDrives && w2.totalDrives) {
    s3Drives = addSeasonRankingByCategory(w1.totalDrives, w2.totalDrives);
  }

  if (
    w1.totalThirdDownAttempts &&
    w2.totalThirdDownAttempts &&
    w1.totalThirdDownConverted &&
    w2.totalThirdDownConverted
  ) {
    s3ThirdDownAttempts = addSeasonRankingByCategory(w1.totalThirdDownAttempts, w2.totalThirdDownAttempts);
    s3ThirdDownConverted = addSeasonRankingByCategory(w1.totalThirdDownConverted, w2.totalThirdDownConverted);
    s3ThirdDownEfficiency = getEfficiencyRankingByCategory(s3ThirdDownConverted, s3ThirdDownAttempts);
  }

  if (w1.totalRedZoneScored && w2.totalRedZoneScored && w1.totalRedZoneAttempts && w2.totalRedZoneAttempts) {
    s3RedZoneScored = addSeasonRankingByCategory(w1.totalRedZoneScored, w2.totalRedZoneScored);
    s3RedZoneAttempts = addSeasonRankingByCategory(w1.totalRedZoneAttempts, w2.totalRedZoneAttempts);
    s3RedZoneEfficiency = getEfficiencyRankingByCategory(s3RedZoneScored, s3RedZoneAttempts);
  }

  if (w1.totalFirstDowns && w2.totalFirstDowns) {
    s3FirstDowns = addSeasonRankingByCategory(w1.totalFirstDowns, w2.totalFirstDowns);
  }

  if (w1.totalFirstDownsFromPenalties && w2.totalFirstDownsFromPenalties) {
    s3FirstDownsFromPenalties = addSeasonRankingByCategory(
      w1.totalFirstDownsFromPenalties,
      w2.totalFirstDownsFromPenalties,
    );
  }

  if (w1.totalFirstDownsFromRushing && w2.totalFirstDownsFromRushing) {
    s3FirstDownsFromRushing = addSeasonRankingByCategory(w1.totalFirstDownsFromRushing, w2.totalFirstDownsFromRushing);
  }

  if (w1.totalFirstDownsFromPassing && w2.totalFirstDownsFromPassing) {
    s3FirstDownsFromPassing = addSeasonRankingByCategory(w1.totalFirstDownsFromPassing, w2.totalFirstDownsFromPassing);
  }

  if (
    w1.totalFourthDownAttempts &&
    w2.totalFourthDownAttempts &&
    w1.totalFourthDownScored &&
    w2.totalFourthDownScored
  ) {
    s3FourthDownAttempts = addSeasonRankingByCategory(w1.totalFourthDownAttempts, w2.totalFourthDownAttempts);
    s3FourthDownScored = addSeasonRankingByCategory(w1.totalFourthDownScored, w2.totalFourthDownScored);
    s3FourthDownEfficiency = getEfficiencyRankingByCategory(s3FourthDownScored, s3FourthDownAttempts);
  }

  // #endregion

  // #region Sort Rankings
  s3TotalYards.sort(sortTeamsByValue).reverse();
  s3Plays.sort(sortTeamsByValue).reverse();
  s3TotalYardsPerPlay.sort(sortTeamsByValue).reverse();
  s3PassingYards.sort(sortTeamsByValue).reverse();
  s3PassCompletions.sort(sortTeamsByValue).reverse();
  s3PassAttempts.sort(sortTeamsByValue).reverse();
  s3PassingPercentage.sort(sortTeamsByValue).reverse();
  s3PassingYardsPerAttempt.sort(sortTeamsByValue).reverse();
  s3RushingYards.sort(sortTeamsByValue).reverse();
  s3RushingAttempts.sort(sortTeamsByValue).reverse();
  s3RushingYardsPerAttempt.sort(sortTeamsByValue).reverse();
  s3PointsScored.sort(sortTeamsByValue).reverse();
  s3TimeOfPossession.sort(sortTeamsByValue).reverse();
  s3InterceptionsThrown.sort(sortTeamsByValue);
  s3FumblesLost.sort(sortTeamsByValue);
  s3Penalties.sort(sortTeamsByValue);
  s3PenaltyYards.sort(sortTeamsByValue);
  s3PassingTDFor.sort(sortTeamsByValue).reverse();
  s3sRushingTDFor.sort(sortTeamsByValue).reverse();
  s3sFGFor.sort(sortTeamsByValue).reverse();
  s3sEPFor.sort(sortTeamsByValue).reverse();
  s3s2PtFor.sort(sortTeamsByValue).reverse();
  s3sTotalTDFor.sort(sortTeamsByValue).reverse();
  s3SackedTimes.sort(sortTeamsByValue);
  s3SackedYardsLost.sort(sortTeamsByValue);
  s3Drives.sort(sortTeamsByValue).reverse();
  s3FirstDowns.sort(sortTeamsByValue).reverse();
  s3FirstDownsFromPenalties.sort(sortTeamsByValue).reverse();
  s3FirstDownsFromRushing.sort(sortTeamsByValue).reverse();
  s3FirstDownsFromPassing.sort(sortTeamsByValue).reverse();
  s3ThirdDownAttempts.sort(sortTeamsByValue).reverse();
  s3ThirdDownConverted.sort(sortTeamsByValue).reverse();
  s3ThirdDownEfficiency.sort(sortTeamsByValue).reverse();
  s3FourthDownScored.sort(sortTeamsByValue).reverse();
  s3FourthDownAttempts.sort(sortTeamsByValue).reverse();
  s3FourthDownEfficiency.sort(sortTeamsByValue).reverse();
  s3RedZoneScored.sort(sortTeamsByValue).reverse();
  s3RedZoneAttempts.sort(sortTeamsByValue).reverse();
  s3RedZoneEfficiency.sort(sortTeamsByValue).reverse();

  // DST
  s3TotalYardsAllowed.sort(sortTeamsByValue);
  s3PassingYardsAllowed.sort(sortTeamsByValue);
  s3RushingYardsAllowed.sort(sortTeamsByValue);
  s3PointsAllowed.sort(sortTeamsByValue);
  s3Sacks.sort(sortTeamsByValue).reverse();
  s3DefensiveInterceptions.sort(sortTeamsByValue).reverse();
  s3FumblesRecovered.sort(sortTeamsByValue).reverse();
  s3Safeties.sort(sortTeamsByValue).reverse();
  s3DefensiveTDs.sort(sortTeamsByValue).reverse();
  s3sPassingTDAgainst.sort(sortTeamsByValue);
  s3sRushingTDAgainst.sort(sortTeamsByValue);
  s3sFGAgainst.sort(sortTeamsByValue);
  s3sEPAgainst.sort(sortTeamsByValue);
  s3s2PtAgainst.sort(sortTeamsByValue);
  s3sTotalTDAgainst.sort(sortTeamsByValue);
  // #endregion

  const getCategory = (title: CategoryNameT, ranking: IRanking[]): IWeekRank => ({
    adjustedRanking: [],
    rankCategory: title,
    ranking,
    weekID: weekID,
  });

  // #region Return Rankings
  const seasonRanking: IRankingCategories = {
    totalDefensiveInterceptions: getCategory(CategoryName.DefensiveInterceptions, s3DefensiveInterceptions),
    totalDefensiveOrSpecialTeamsTds: getCategory(CategoryName.DefensiveTDs, s3DefensiveTDs),
    totalForcedFumbles: getCategory(CategoryName.FumblesRecovered, s3FumblesRecovered),
    totalFumblesLost: getCategory(CategoryName.FumblesLost, s3FumblesLost),
    totalInterceptionsThrown: getCategory(CategoryName.InterceptionsThrown, s3InterceptionsThrown),
    totalPassAttempts: getCategory(CategoryName.PassAttempts, s3PassAttempts),
    totalPassCompletions: getCategory(CategoryName.PassCompletions, s3PassCompletions),
    totalPassingPercentage: getCategory(CategoryName.PassingPercentage, s3PassingPercentage),
    totalPassingYards: getCategory(CategoryName.PassingYards, s3PassingYards),
    totalPassingYardsAllowed: getCategory(CategoryName.PassingYardsAllowed, s3PassingYardsAllowed),
    totalPassingYardsPerAttempt: getCategory(CategoryName.PassingYardsPerAttempt, s3PassingYardsPerAttempt),
    totalPenalties: getCategory(CategoryName.Penalties, s3Penalties),
    totalPenaltyYards: getCategory(CategoryName.PenaltyYards, s3PenaltyYards),
    totalPlays: getCategory(CategoryName.TotalPlays, s3Plays),
    totalPointsAllowed: getCategory(CategoryName.PointsAllowed, s3PointsAllowed),
    totalPointsScored: getCategory(CategoryName.PointsScored, s3PointsScored),
    totalRushingAttempts: getCategory(CategoryName.RushingAttempts, s3RushingAttempts),
    totalRushingYards: getCategory(CategoryName.RushingYards, s3RushingYards),
    totalRushingYardsAllowed: getCategory(CategoryName.RushingYardsAllowed, s3RushingYardsAllowed),
    totalRushingYardsPerAttempt: getCategory(CategoryName.RushingYardsPerAttempt, s3RushingYardsPerAttempt),
    totalSacks: getCategory(CategoryName.Sacks, s3Sacks),
    totalSafeties: getCategory(CategoryName.Safeties, s3Safeties),
    totalScoring2PtAgainst: getCategory(CategoryName.Allowed2ptConversions, s3s2PtAgainst),
    totalScoring2PtFor: getCategory(CategoryName.Scored2ptConversions, s3s2PtFor),
    totalScoringEPAgainst: getCategory(CategoryName.AllowedExtraPoints, s3sEPAgainst),
    totalScoringEPFor: getCategory(CategoryName.ScoringEPFor, s3sEPFor),
    totalScoringFGAgainst: getCategory(CategoryName.AllowedFGs, s3sFGAgainst),
    totalScoringFGFor: getCategory(CategoryName.ScoredFGs, s3sFGFor),
    totalScoringPassingTDAgainst: getCategory(CategoryName.AllowedPassingTDs, s3sPassingTDAgainst),
    totalScoringPassingTDFor: getCategory(CategoryName.ScoredPassingTDs, s3PassingTDFor),
    totalScoringRushingTDAgainst: getCategory(CategoryName.AllowedRushingTDs, s3sRushingTDAgainst),
    totalScoringRushingTDFor: getCategory(CategoryName.ScoredRushingTDs, s3sRushingTDFor),
    totalScoringTotalTDAgainst: getCategory(CategoryName.TotalTDsAllowed, s3sTotalTDAgainst),
    totalScoringTotalTDFor: getCategory(CategoryName.TotalTDsScored, s3sTotalTDFor),
    totalTimeOfPossession: getCategory(CategoryName.TimeOfPossession, s3TimeOfPossession),
    totalYards: getCategory(CategoryName.TotalYards, s3TotalYards),
    totalYardsAllowed: getCategory(CategoryName.TotalYardsAllowed, s3TotalYardsAllowed),
    totalYardsPerPlay: getCategory(CategoryName.TotalYardsPerPlay, s3TotalYardsPerPlay),
    totalSackedTimes: getCategory(CategoryName.TotalSackedTimes, s3SackedTimes),
    totalSackedYardsLost: getCategory(CategoryName.TotalSackedYardsLost, s3SackedYardsLost),
    totalDrives: getCategory(CategoryName.TotalDrives, s3Drives),
    totalFirstDowns: getCategory(CategoryName.TotalFirstDowns, s3FirstDowns),
    totalFirstDownsFromPenalties: getCategory(CategoryName.FirstDownsFromPenalties, s3FirstDownsFromPenalties),
    totalFirstDownsFromRushing: getCategory(CategoryName.FirstDownsFromRushing, s3FirstDownsFromRushing),
    totalFirstDownsFromPassing: getCategory(CategoryName.FirstDownsFromPassing, s3FirstDownsFromPassing),
    totalThirdDownAttempts: getCategory(CategoryName.ThirdDownAttempts, s3ThirdDownAttempts),
    totalThirdDownConverted: getCategory(CategoryName.ThirdDownConverted, s3ThirdDownConverted),
    totalThirdDownEfficiency: getCategory(CategoryName.ThirdDownEfficiency, s3ThirdDownEfficiency),
    totalFourthDownScored: getCategory(CategoryName.FourthDownScored, s3FourthDownScored),
    totalFourthDownAttempts: getCategory(CategoryName.FourthDownAttempts, s3FourthDownAttempts),
    totalFourthDownEfficiency: getCategory(CategoryName.FourthDownEfficiency, s3FourthDownEfficiency),
    totalRedZoneScored: getCategory(CategoryName.RedZoneScored, s3RedZoneScored),
    totalRedZoneAttempts: getCategory(CategoryName.RedZoneAttempts, s3RedZoneAttempts),
    totalRedZoneEfficiency: getCategory(CategoryName.RedZoneEfficiency, s3RedZoneEfficiency),
  };
  // #endregion

  return seasonRanking;
};

/**
 * Get the total number of completed weeks from all seasons up to and including the given week.
 * Seasons start from 2024 ending at the current season in the Season enum.
 * Assumes each season has NFL_WEEKS_TOTAL weeks.
 *
 * @param {SeasonWeekID} seasonWeekID - ex: '2023-05'
 *
 * @return {number} - Total number of completed weeks from all seasons up to and including the given week.
 */
export const getTotalCompletedWeeksCount = (seasonWeekID: SeasonWeekID): number => {
  const totalYears = Object.keys(Season).length;
  const maxWeekNum = toNumber(breakSeasonWeekID(seasonWeekID).weekID);
  const maxCount = (totalYears - 1) * NFL_WEEKS_TOTAL + maxWeekNum;
  return maxCount;
};

export const createAllAvailableSeasonWeekIDs = (): SeasonWeekID[] => {
  const allSeasonWeekIDs: SeasonWeekID[] = [];

  Object.entries(Season).forEach((item) => {
    const yearID = item[1];

    for (let week = 1; week <= NFL_WEEKS_TOTAL; week++) {
      allSeasonWeekIDs.push(toSeasonWeekID(yearID, toWeekID(week)));
    }
  });
  return allSeasonWeekIDs;
};

export const getAllSeasonWeekIDsForSeasonID = (seasonID: Season): SeasonWeekID[] => {
  const allSeasonWeekIDs: SeasonWeekID[] = [];
  for (let week = 1; week <= NFL_WEEKS_TOTAL; week++) {
    allSeasonWeekIDs.push(toSeasonWeekID(seasonID, toWeekID(week)));
  }
  return allSeasonWeekIDs;
};

export const getAllSeasonWeekIDsUpToSeasonWeekID = (seasonWeekID: SeasonWeekID): SeasonWeekID[] => {
  const yearID = breakSeasonWeekID(seasonWeekID).yearID as Season;
  const allSeasonWeekIDs: SeasonWeekID[] = getAllSeasonWeekIDsForSeasonID(yearID);
  const targetIndex = allSeasonWeekIDs.indexOf(seasonWeekID);
  return allSeasonWeekIDs.slice(0, targetIndex + 1);
};
